import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  Box,
  Chip,
  alpha,
  CircularProgress,
  Divider,
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import dayjs from "dayjs";
import { getPatientHistory } from "../services/pcpAssignmentService";

const REASON_CONFIG = {
  INITIAL_ASSIGNMENT: { label: "Assigned", bg: "#D1FAE5", color: "#065F46" },
  MANUAL_REASSIGN: { label: "Reassigned", bg: "#DBEAFE", color: "#1E40AF" },
  MANUAL_UNASSIGN: { label: "Unassigned", bg: "#FEE2E2", color: "#991B1B" },
  AUTO_REASSIGN: { label: "Auto Reassigned", bg: "#E0E7FF", color: "#3730A3" },
  PCP_DEACTIVATED: { label: "PCP Deactivated", bg: "#FEF3C7", color: "#92400E" },
  PATIENT_DEACTIVATED: { label: "Patient Deactivated", bg: "#F3F4F6", color: "#6B7280" },
};

const SOURCE_LABELS = {
  MANUAL: "Manual",
  AUTO_HIGHRISK: "Auto (High Risk)",
  AUTO_HIGHCOST: "Auto (High Cost)",
  AUTO_MEDICARE: "Auto (Medicare)",
  AUTO_AGE: "Auto (Age Match)",
  AUTO_CRITICAL: "Auto (Critical)",
  AUTO_REMAINING: "Auto (Remaining)",
};

export default function PatientHistoryDialog({ cipId, patientName, open, onClose }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !cipId) return;
    let cancelled = false;
    setLoading(true);
    getPatientHistory(cipId)
      .then((data) => { if (!cancelled) setHistory(data || []); })
      .catch(() => { if (!cancelled) setHistory([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, cipId]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <Box sx={{ background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)", px: 3, py: 2.5 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <HistoryIcon sx={{ color: "white" }} />
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ color: "white" }}>
              Assignment History
            </Typography>
            <Typography variant="body2" sx={{ color: alpha("#fff", 0.8) }}>
              {patientName || cipId}
            </Typography>
          </Box>
        </Stack>
      </Box>

      <DialogContent sx={{ px: 3, py: 2.5 }}>
        {loading ? (
          <Stack alignItems="center" sx={{ py: 4 }}>
            <CircularProgress size={32} />
          </Stack>
        ) : history.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
            No assignment history found.
          </Typography>
        ) : (
          <Stack spacing={0}>
            {history.map((entry, idx) => {
              const reasonCfg = REASON_CONFIG[entry.changeReason] || { label: entry.changeReason, bg: "#F3F4F6", color: "#374151" };
              const isCurrent = !entry.assignedTo;
              return (
                <React.Fragment key={entry.historyId}>
                  {idx > 0 && <Divider sx={{ my: 1.5 }} />}
                  <Stack spacing={0.75}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                          label={reasonCfg.label}
                          size="small"
                          sx={{ height: 22, fontSize: "0.68rem", fontWeight: 700, bgcolor: reasonCfg.bg, color: reasonCfg.color }}
                        />
                        {isCurrent && (
                          <Chip label="Current" size="small" sx={{ height: 20, fontSize: "0.6rem", fontWeight: 700, bgcolor: "#D1FAE5", color: "#065F46" }} />
                        )}
                        {entry.crossLocation && (
                          <Chip label="Cross-Location" size="small" sx={{ height: 20, fontSize: "0.6rem", fontWeight: 700, bgcolor: "#FEF3C7", color: "#92400E" }} />
                        )}
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        {entry.createDate ? dayjs(entry.createDate).format("MMM D, YYYY h:mm A") : "—"}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" fontWeight={600}>
                      {entry.pcpName} — {entry.centerName}
                    </Typography>
                    <Stack direction="row" spacing={2}>
                      <Typography variant="caption" color="text.secondary">
                        Source: {SOURCE_LABELS[entry.assignmentSource] || entry.assignmentSource}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        From: {dayjs(entry.assignedFrom).format("MMM D, YYYY")}
                        {entry.assignedTo ? ` — To: ${dayjs(entry.assignedTo).format("MMM D, YYYY")}` : " — Present"}
                      </Typography>
                    </Stack>
                    {entry.actorName && (
                      <Typography variant="caption" color="text.disabled">
                        By: {entry.actorName}
                      </Typography>
                    )}
                  </Stack>
                </React.Fragment>
              );
            })}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 2, textTransform: "none" }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
